import { defineStore } from 'pinia';
import axios from 'axios';
import { filterHandlers } from '@/utils/filterProductsUtils'
import { updateCharFilterOptions, updateNumberFilterRange } from '@/utils/filterFiltersUtils';
// import { useRouter } from 'vue-router';

export const useProductsStore = defineStore('products', {
  // State
  state: () => {
    return {
      initialProducts: [],
      productsWithFilters: [],
      domain: {},
      products: [],
      productsData: {},
      filteredProducts: [],
      filterAttributes: {},
      filters: [],
      initialFilters: {},
      appliedFilters: {},
      hasActiveFilters: false,
      pageSize: 12,
      page: 1,
      productTabs: [],
      pageInfo: {},
      currentProductIndex: 1,
      section: {},
      filterUpdateIndex: 1,
      filteredFilters: [],
      activeFilters: [],
      // pageName: '',
    }
  },

  // Actions
  actions: {
    async fetchProductsWithFilters() {
      try { 
        const response = await axios.get('https://products-json.pages.dev/lagertechnik-extended.json'); // Replace with your API endpoint
        this.productsWithFilters = response.data; // Directly update the state
        this.products = response.data.data.products.slice(0, this.pageSize)
        this.initialProducts = response.data.data.products
        this.filters = JSON.parse(JSON.stringify(response.data.config.filter.attributes));
        this.filteredProducts = response.data.data.products
        const { products, ...productsData } = response.data.data
        this.productsData = productsData
        this.productTabs = response.data.config.product?.tabs || []
        this.domain = response.data.domain
        this.pageInfo = response.data.page
        // this.pageName = response.data.data.products[0].url.split('/')[1]
        this.filterAttributes = {
          ...response.data.config.filter,
          links: response.data.config.filter.links || [],
        };
        // @TODO what is section?
        this.section = {
          ...response.data.section,
          isSimpleSection: false,
          full_width: !!response.data.section.isFullWidth || false,
          settings: { },
        };
        this.resetFilters()

      } catch (error) {
        console.error('Failed to fetch products:', error);
      }
    },

    updateAppliedFilters(filter) {
      const filterName = Object.keys(filter)[0]
      const filterValue = Object.values(filter)[0]
      // for (const filterElement in filter) {
        this.appliedFilters[filterName] = filterValue
      // }

    },

    filterFilters(filter) {
      // Ensure we have filters to work with
      if (!this.filterAttributes.attributes.length) return;

      // Clone the original filters as a backup so we always start fresh
      const referenceFilters = JSON.parse(JSON.stringify(this.filters));

      this.filterAttributes.attributes.forEach(attr => {
        // Skip the filter that is currently being changed or if it’s already applied
        if (attr.name === filter) return;
        if (Object.keys(this.appliedFilters).includes(attr.name)) return;

        // --- CHAR FILTERS ---
        if (attr.type === 'char') {
          const originalFilter = referenceFilters.find(f => f.title === attr.title);
          if (!originalFilter) return;

          // Update the options available in the dropdown based on filtered products
          attr.values = updateCharFilterOptions(originalFilter, attr, this.filteredProducts);
        }
        // --- NUMBER FILTERS (number-slider and number-input) ---
        else if (attr.type === 'number-slider' || attr.type === 'number-input') {
          const originalFilter = referenceFilters.find(f => f.title === attr.title);
          if (!originalFilter) return;

          // Update the numeric range for the filter
          this.initialFilters[attr.name] = updateNumberFilterRange(originalFilter, attr, this.filteredProducts);
        }
      });
    },

    setHasActiveFilter() {
      this.hasActiveFilters = true
    },

    loadMore() {
      const sourceArray = this.hasActiveFilters ? this.filteredProducts : this.initialProducts;
      this.products = sourceArray.slice(0, this.pageSize * this.page);
    },

    addPage() {
      this.page++
    },

    filterProducts() {
      let filtered = [...this.initialProducts];

      for (const [filterName, filterValue] of Object.entries(this.appliedFilters)) {
        // Map similar types to a unified handler key
        const rawFilterType = this.filterAttributes.attributes.find(attr => attr.name === filterName)?.type;
        const filterType = rawFilterType === 'number-slider' || rawFilterType === 'number-input'
          ? 'number-range'
          : rawFilterType;

        // Select the appropriate handler or fallback to the default one
        const handler = filterHandlers[filterType] || filterHandlers.default;

        // Apply the filter
        filtered = handler(filtered, filterName, filterValue);
      }

      this.filteredProducts = filtered;
    },

    applyFilters() {
      this.products = this.filteredProducts.slice(0, this.pageSize * this.page);
      this.activeFilters = { ...this.appliedFilters }
      this.setHasActiveFilter();
    },

    removeFilter(filter) {
      delete this.appliedFilters[filter]
      delete this.activeFilters[filter]
      if(Object.keys(this.appliedFilters).length === 0) {
        this.removeFilters()
      }
    },

    removeFilters() {
      this.products = this.initialProducts.slice(0, this.pageSize);
      this.hasActiveFilters = false;
      this.appliedFilters = {};
      this.resetFilters()
      this.page = 1; // Reset page count
    },

    goToPrevProduct() {
      this.currentProductIndex--
    },

    goToNextProduct() {
      this.currentProductIndex++
    },

    goToFirstProduct() {
      this.currentProductIndex = 1
    },

    setCurrentProductIndex(value) {
      this.currentProductIndex = value
    },

    applyCharFilter(filter, value) {
      this.appliedFilters[filter] = value
    },
    applyRangeFilter(filter, index, e) {
      this.appliedFilters[filter][index] = +e.target.value
    },
    resetFilter(attribute) {
      if (attribute.type === 'number-input' || attribute.type === 'number-slider') {
        this.appliedFilters[attribute.name] = [attribute.range?.min || null, attribute.range?.max || null];
      } else if(attribute.type === 'boolean') {
        this.appliedFilters[attribute.name] = false;
      }
      else {
        this.appliedFilters[attribute.name] = '';
      }
    },

    resetFilters() {
      this.filterAttributes.attributes = JSON.parse(JSON.stringify(this.filters))
      this.initialFilters = this.filters.reduce((acc, attribute) => {
        if (attribute.type === 'number-input' || attribute.type === 'number-slider') {
          acc[attribute.name] = [attribute.range?.min || null, attribute.range?.max || null];
        } else if (attribute.type === 'boolean') {
          acc[attribute.name] = false;
        } else {
          acc[attribute.name] = '';
        }
        return acc;
      }, {});
    }

  },

  getters: {
    filterCount () {
      return Object.keys(this.appliedFilters).length > 0 ? this.filteredProducts.length : this.initialProducts.length
    },
  },
});
